import { Grid, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import { Fragment } from 'react';
import { z } from 'zod';

import { Footer } from '@/components/Footer';
import { Header } from '@/components/Header';
import { AdministrationBody } from '@/components/management/layout/AdministrationBody';
import { AdministrationHeader } from '@/components/management/layout/AdministrationHeader';
import { PasswordResetForm } from '@/components/public/PasswordResetForm';
import { backendApiPasswordResetSubmitMutation } from '@/modules/api/backend/@tanstack/react-query.gen';
import { useSnackbarStore } from '@/stores/useSnackbarStore';

const defaultSearchValues = {
  token: '',
};

const zValidateSearch = z.object({
  token: z.string().default(defaultSearchValues.token).catch(defaultSearchValues.token),
});

export const Route = createFileRoute('/_public/_authentication/password-reset/$authenticationTokenId')({
  validateSearch: zValidateSearch,
  search: {
    middlewares: [stripSearchParams(defaultSearchValues)],
  },
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: PasswordResetSubmitRoute,
});

function PasswordResetSubmitRoute() {
  const { authenticationTokenId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { token } = Route.useSearch();

  const { openSnackbar } = useSnackbarStore((state) => state.actions);

  const { mutateAsync } = useMutation({
    ...backendApiPasswordResetSubmitMutation(),
    meta: { invalidateQueries: false },
    onSuccess: () => {
      openSnackbar({
        color: 'success',
        message: 'Ihr Passwort wurde erfolgreich zurückgesetzt!',
      });
      navigate({ to: '/login' });
    },
    onError: () => {
      openSnackbar({
        color: 'error',
        message: 'Fehler beim Zurücksetzen des Passworts. Bitte versuchen Sie es erneut.',
      });
    },
  });

  return (
    <Fragment>
      <Header />
      <AdministrationHeader label="Passwort zurücksetzen" />
      <AdministrationBody>
        <Grid
          container
          direction={{ sm: 'column', lg: 'row' }}
          spacing={{ sm: 2, lg: 13 }}
          sx={{ pt: { xs: 8, lg: 0 } }}
        >
          <Grid size={{ sm: 12, lg: 5.5 }} sx={{ mx: 'auto' }}>
            <PasswordResetForm
              onSubmit={async (value) => {
                await mutateAsync({
                  path: { authenticationTokenId },
                  query: { token },
                  body: value,
                });
              }}
            />
          </Grid>
        </Grid>
      </AdministrationBody>
      <Stack
        component="footer"
        sx={{
          display: { xs: 'none', lg: 'flex' },
          position: 'fixed',
          bottom: 0,
          width: '100%',
        }}
      >
        <Footer />
      </Stack>
    </Fragment>
  );
}
